import type { PlayerApi } from '@movingimage-evp/player-library';

import { PlayerEvent } from '@movingimage-evp/player-library';
import { useCallback, useEffect, useRef, useState } from 'react';

import { devLog } from '../../../../../utils';

export interface AutoplayUnmuteResult {
  unmutePopupVisible: boolean;
  hideUnmutePopup: () => void;
  tryAutoplay: (api: PlayerApi) => Promise<void>;
  unmute: (api: PlayerApi) => void;
}

/**
 * Hook to manage autoplay and unmute logic for a video player.
 */
export function useAutoplayUnmute(): AutoplayUnmuteResult {
  const [unmutePopupVisible, setUnmutePopupVisible] = useState(false);

  const playerApiRef = useRef<PlayerApi | null>(null);

  const hideUnmutePopup = useCallback(() => {
    setUnmutePopupVisible(false);
  }, []);

  const showUnmutePopup = useCallback(() => {
    setUnmutePopupVisible(true);
  }, []);

  const handleVolumeChange = useCallback(() => {
    if (!playerApiRef.current?.getMuted()) {
      setUnmutePopupVisible(false);
    }
  }, []);

  useEffect(() => {
    if (!unmutePopupVisible) {
      playerApiRef.current?.removeEventListener(PlayerEvent.VOLUME_CHANGE, handleVolumeChange);
    }
  }, [handleVolumeChange, unmutePopupVisible]);

  const tryAutoplay = useCallback(
    async (api: PlayerApi) => {
      try {
        if (!api.getPaused()) {
          return;
        }

        playerApiRef.current = api;

        await api.autoplay();

        if (api.getMuted()) {
          api.addEventListener(PlayerEvent.VOLUME_CHANGE, handleVolumeChange);
          showUnmutePopup();
        }
      } catch {
        devLog('Autoplay not possible');
      }
    },
    [handleVolumeChange, showUnmutePopup]
  );

  const unmute = useCallback(
    (api: PlayerApi) => {
      playerApiRef.current = api;
      hideUnmutePopup();
      api.setMuted(false);
    },
    [hideUnmutePopup]
  );

  return {
    unmutePopupVisible,
    hideUnmutePopup,
    tryAutoplay,
    unmute,
  };
}
